import type { TaskCategory } from './constants.js';
import type { Task } from './schemas/task.js';
import { TASK_TEMPLATE, type TemplateTask } from './taskTemplate.js';

/**
 * The checklist: pure arithmetic over dates and rows, with no storage and no
 * clock of its own. Every function here takes `today` rather than reading it,
 * because a test that cannot fix the date is a test that fails one morning in
 * February for no reason anybody can reproduce.
 *
 * All dates are `YYYY-MM-DD` and all arithmetic is UTC. A wedding date is a
 * calendar day, not an instant: parsing it in the browser's zone makes
 * `2028-02-12` become the 11th for anyone west of Greenwich, and a checklist
 * that is a day out everywhere in the Americas is worse than no checklist.
 */

const DAY_MS = 86_400_000;

/** `YYYY-MM-DD` to a UTC-midnight epoch. Returns NaN for anything malformed. */
export function parseDate(date: string): number {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date);
  if (!m) return NaN;
  const [y, mo, d] = [Number(m[1]), Number(m[2]), Number(m[3])];
  const ts = Date.UTC(y, mo - 1, d);
  // Date.UTC rolls 2026-02-31 forward to March. A date that does not survive the
  // round trip was never a real day.
  const back = new Date(ts);
  if (back.getUTCMonth() !== mo - 1 || back.getUTCDate() !== d) return NaN;
  return ts;
}

export function formatDate(ts: number): string {
  return new Date(ts).toISOString().slice(0, 10);
}

export function isValidDate(date: string): boolean {
  return !Number.isNaN(parseDate(date));
}

/** Whole days from `from` to `to`; negative when `to` is in the past. */
export function daysBetween(from: string, to: string): number {
  return Math.round((parseDate(to) - parseDate(from)) / DAY_MS);
}

export function addDays(date: string, days: number): string {
  return formatDate(parseDate(date) + days * DAY_MS);
}

/**
 * When a template task falls due.
 *
 * Clamped to `today`, which is the one piece of judgement in this file. A couple
 * who starts planning four months out would otherwise open the tab to thirty
 * tasks dated last year, which reads as an accusation rather than a plan. "All
 * of this is due now" is both kinder and, at four months out, true.
 */
export function templateDueDate(weddingDate: string, daysBefore: number, today: string): string {
  const anchor = addDays(weddingDate, -daysBefore);
  return parseDate(anchor) < parseDate(today) ? today : anchor;
}

export interface GeneratedTask {
  templateKey: string;
  title: string;
  category: TaskCategory;
  daysBefore: number;
  dueDate: string;
}

/**
 * The template tasks a wedding does not have yet.
 *
 * Idempotent by `templateKey`: generating a second time adds only what is
 * missing, so a couple who deleted "Book the florist" because they are growing
 * their own does not get it back, and a template that gains a row later can be
 * topped up without duplicating the forty-two already there.
 */
export function generateTasks(
  weddingDate: string,
  existing: readonly Pick<Task, 'templateKey'>[],
  today: string,
  template: readonly TemplateTask[] = TASK_TEMPLATE,
): GeneratedTask[] {
  const have = new Set(existing.map((t) => t.templateKey).filter(Boolean));
  return template
    .filter((t) => !have.has(t.key))
    .map((t) => ({
      templateKey: t.key,
      title: t.title,
      category: t.category,
      daysBefore: t.daysBefore,
      dueDate: templateDueDate(weddingDate, t.daysBefore, today),
    }));
}

/**
 * Which generated tasks a new wedding date would move, and where to.
 *
 * Only unfinished template tasks: a done task's date is a record of what the
 * plan was, and moving it would rewrite history for no benefit. Tasks typed by
 * hand are never touched — they have no anchor, so there is nothing to
 * recompute, and guessing one would move a date somebody chose deliberately.
 *
 * A task already sitting on the right date is left out, which is what lets the
 * caller use an empty result to mean "nothing to do" and skip the write.
 *
 * **The clamp makes "already right" subtler than comparing two dates.** A task
 * generated close to the wedding was clamped to its generation day, so
 * recomputing it tomorrow yields tomorrow, and the naive comparison would call
 * it stale every morning for the rest of the engagement — a banner that never
 * clears, and a button that pushes the task forward a day each time it is
 * pressed, so something never done would never become late either.
 *
 * The rule that avoids it: once both the anchor and the stored date are in the
 * past, the task is already saying "this is due now, or it is late", and no
 * arithmetic on the wedding date can make that more true. Leave it alone. A
 * stored date still in the future is a different matter — if the wedding moved
 * up, that date can now fall after the wedding itself, and it does have to move.
 */
export function rescheduleTasks(
  tasks: readonly Task[],
  weddingDate: string,
  today: string,
): { taskId: string; dueDate: string }[] {
  return tasks.flatMap((t) => {
    if (t.done || t.daysBefore === undefined) return [];

    const anchor = addDays(weddingDate, -t.daysBefore);
    const settled = parseDate(anchor) <= parseDate(today) && !!t.dueDate && t.dueDate <= today;
    if (settled) return [];

    const dueDate = templateDueDate(weddingDate, t.daysBefore, today);
    return dueDate === t.dueDate ? [] : [{ taskId: t.taskId, dueDate }];
  });
}

export interface TaskStats {
  total: number;
  done: number;
  /** Not done, dated, and the date has passed. */
  overdue: number;
  /** Not done and due within the next 30 days, overdue excluded. */
  dueSoon: number;
  /** Not done and carrying no date at all. */
  undated: number;
}

export function taskStats(tasks: readonly Task[], today: string): TaskStats {
  const stats: TaskStats = { total: tasks.length, done: 0, overdue: 0, dueSoon: 0, undated: 0 };
  for (const t of tasks) {
    if (t.done) {
      stats.done += 1;
      continue;
    }
    if (!t.dueDate) {
      stats.undated += 1;
      continue;
    }
    const days = daysBetween(today, t.dueDate);
    if (days < 0) stats.overdue += 1;
    else if (days <= 30) stats.dueSoon += 1;
  }
  return stats;
}

export const TASK_BUCKETS = ['overdue', 'now', 'soon', 'later', 'undated', 'done'] as const;
export type TaskBucket = (typeof TASK_BUCKETS)[number];

/**
 * Which heading a task belongs under.
 *
 * Buckets run from today, not from the wedding: "what is on me this month" is
 * the question a checklist is opened to answer, and "nine to twelve months
 * before the wedding" is the same answer dressed as trivia.
 */
export function taskBucket(task: Task, today: string): TaskBucket {
  if (task.done) return 'done';
  if (!task.dueDate) return 'undated';
  const days = daysBetween(today, task.dueDate);
  if (days < 0) return 'overdue';
  if (days <= 30) return 'now';
  if (days <= 90) return 'soon';
  return 'later';
}

/** Earliest first; undated last; done last of all, most recently finished first. */
export function sortTasks(tasks: readonly Task[]): Task[] {
  return [...tasks].sort((a, b) => {
    if (a.done !== b.done) return a.done ? 1 : -1;
    if (a.done && b.done) return (b.doneAt ?? '').localeCompare(a.doneAt ?? '');
    if (!a.dueDate !== !b.dueDate) return a.dueDate ? -1 : 1;
    if (a.dueDate && b.dueDate && a.dueDate !== b.dueDate) {
      return a.dueDate.localeCompare(b.dueDate);
    }
    return a.title.localeCompare(b.title);
  });
}

export interface TaskGroup {
  bucket: TaskBucket;
  tasks: Task[];
}

/** Sorted tasks under their buckets, in `TASK_BUCKETS` order, empties dropped. */
export function groupTasks(tasks: readonly Task[], today: string): TaskGroup[] {
  const sorted = sortTasks(tasks);
  return TASK_BUCKETS.map((bucket) => ({
    bucket,
    tasks: sorted.filter((t) => taskBucket(t, today) === bucket),
  })).filter((g) => g.tasks.length > 0);
}

export interface TaskDigest {
  overdue: Task[];
  dueSoon: Task[];
  /** Everything still open, so the mail can say how much is left without a second pass. */
  remaining: number;
}

/**
 * What a weekly reminder is allowed to be about.
 *
 * Overdue and the next `horizonDays`, nothing else. A digest listing everything
 * outstanding is a digest that looks identical every week, and a mail that looks
 * identical every week stops being opened — at which point the one that mattered
 * goes unread too.
 *
 * Returns empty lists when there is nothing to say, which is what lets the
 * sender skip the send rather than mail somebody to tell them about nothing.
 */
export function taskDigest(tasks: readonly Task[], today: string, horizonDays = 14): TaskDigest {
  const open = tasks.filter((t) => !t.done);
  const horizon = addDays(today, horizonDays);

  return {
    overdue: sortTasks(open.filter((t) => t.dueDate !== undefined && t.dueDate < today)),
    dueSoon: sortTasks(
      open.filter((t) => t.dueDate !== undefined && t.dueDate >= today && t.dueDate <= horizon),
    ),
    remaining: open.length,
  };
}

/** True when there is something worth an email. */
export const digestIsWorthSending = (digest: TaskDigest): boolean =>
  digest.overdue.length > 0 || digest.dueSoon.length > 0;
